import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trophy, TrendingUp, Users, Target } from "lucide-react";
import { toast } from "sonner";

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/team/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Erreur chargement"));
  }, []);

  if (!data) return <AppShell><div className="grid place-items-center py-24"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div></AppShell>;

  const leader = data.members[0];

  return (
    <AppShell>
      <div className="px-5 pt-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-full -ml-2" data-testid="back"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
      </div>
      <div className="px-5 mt-2">
        <h1 className="font-display text-2xl font-bold text-[var(--text)]" data-testid="manager-title">Dashboard manager</h1>
        <p className="text-sm text-zinc-500">Performance de l'équipe en temps réel.</p>
      </div>

      {/* Totals */}
      <div className="px-5 mt-4 grid grid-cols-3 gap-2">
        <Stat icon={Users} label="Membres" value={data.totals.members} color="text-sky-600 bg-sky-50" />
        <Stat icon={Target} label="Actions" value={data.totals.actions} color="text-emerald-400 bg-emerald-500/10" />
        <Stat icon={Trophy} label="Conversions" value={data.totals.conversions} color="text-emerald-300 bg-emerald-500/10" />
      </div>

      {/* Leaderboard */}
      {leader && (
        <div className="px-5 mt-5">
          <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-500 to-amber-500 text-white shadow-lg" data-testid="leader-card">
            <div className="text-xs font-bold uppercase tracking-wider opacity-90">🏆 Top performeur</div>
            <div className="font-display text-xl font-bold mt-1">{leader.name}</div>
            <div className="text-xs opacity-90 mt-0.5">{leader.converted} conversions · {leader.xp} XP · Canal préféré : {leader.best_channel}</div>
          </div>
        </div>
      )}

      <div className="px-5 mt-5">
        <h2 className="font-display font-bold text-[var(--text)] mb-2">Classement</h2>
        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border-soft)] shadow-sm divide-y overflow-hidden">
          {data.members.map((m, i) => (
            <div key={m.user_id} className="p-3 flex items-center gap-3" data-testid={`rank-${m.user_id}`}>
              <div className={`w-8 h-8 rounded-full grid place-items-center font-bold text-sm ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-200 text-zinc-300" : i === 2 ? "bg-emerald-500/15 text-emerald-300" : "bg-[var(--surface-alt)] text-zinc-500"}`}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[var(--text)] truncate">{m.name}</div>
                <div className="text-xs text-zinc-500 truncate">{m.role} · Canal : {m.best_channel}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-lg font-bold text-[var(--text)]">{m.converted}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Conversions</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-member details */}
      <div className="px-5 mt-5">
        <h2 className="font-display font-bold text-[var(--text)] mb-2">Détail par commercial</h2>
        <div className="space-y-2">
          {data.members.map((m) => (
            <div key={m.user_id} className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm" data-testid={`detail-${m.user_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-[var(--text)]">{m.name}</div>
                  <div className="text-xs text-zinc-500">{m.email}</div>
                </div>
                <span className="text-[10px] bg-emerald-500/15 text-emerald-300 rounded-full px-2 py-0.5 font-bold uppercase tracking-wider">{m.role}</span>
              </div>
              <div className="grid grid-cols-5 gap-1 mt-3 text-center">
                <Mini label="Prospects" v={m.prospects_week} />
                <Mini label="Actions" v={m.interactions} />
                <Mini label="RDV" v={m.rdv} />
                <Mini label="Conv." v={m.converted} />
                <Mini label="XP" v={m.xp} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[var(--surface)] rounded-xl p-3 border border-[var(--border-soft)] shadow-sm">
      <div className={`w-8 h-8 rounded-lg grid place-items-center ${color}`}><Icon className="w-4 h-4" /></div>
      <div className="font-display text-xl font-bold text-[var(--text)] mt-2">{value}</div>
      <div className="text-[10px] font-medium text-zinc-500 leading-tight uppercase tracking-wider">{label}</div>
    </div>
  );
}
function Mini({ label, v }) {
  return (
    <div className="bg-[var(--surface-alt)] rounded-lg p-2">
      <div className="font-display text-base font-bold text-[var(--text)]">{v}</div>
      <div className="text-[9px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
    </div>
  );
}
